/**
 * ModuleDetailPanel — Panel de detalle (preview iframe + acciones)
 * de un Trabajo seleccionado en el Workspace.
 *
 * Extraído de InternalWorkspace.jsx en el refactor 2026-02-08 (cero
 * cambios funcionales: mismas props, mismos data-testids, mismo
 * fullscreen, misma detección de diagrama legacy).
 */
import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
  Eye, X, Check, Pencil, Sparkles, FileCode, RefreshCw, Lock,
  Maximize2, Minimize2, Rocket, GitCompare, Save, Edit3,
  History, Smartphone, Download, Link2,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { UnifiedCodePreview } from '../UnifiedCodePreview';
import ProjectTreeViewer from '../ProjectTreeViewer';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import buildRenderableCode from '../../utils/buildRenderableCode';
import { API_URL } from '../../config';
// Lazy: DiagramEditor pesa 471 LOC + dependencia `mermaid` (~270KB minified).
// Solo se monta cuando isDiagram && isEditingDiagram. Sacarlo del bundle
// inicial libera ~270KB del Workspace para usuarios que nunca editan
// diagramas Mermaid (la mayoria — los modulos por defecto son Dashboard/code).
const DiagramEditor = lazy(() => import('./DiagramEditor'));
// 🟩 P26.13 + P26.14 · Modales lazy (solo montan cuando se abren)
const DiffModal = lazy(() => import('./DiffModal'));
// 🟥 P26.40 · Deploy real (enlace propio + subdominio). Reemplaza al modal de
// publicación viejo y atiende también el Deploy que la app generada dispara
// desde el iframe de la vista previa.
const DeployModal = lazy(() => import('./DeployModal'));
// 🟥 P26.40 · `PublishProjectModal` quedó reemplazado por `DeployModal`.
// 🟩 P26.30 · Historial de versiones (últimas 5).
const VersionsModal = lazy(() => import('./VersionsModal'));
// 🟩 P26.31 · Enlace temporal de vista previa.
const SharePreviewModal = lazy(() => import('./SharePreviewModal'));

export const ModuleDetailPanel = ({ module, onUpdate, onClose, onRequestEdit, onViewCode, onChangeType, quotaStatus }) => {
  const { language } = useLanguage();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(module?.name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingDiagram, setIsEditingDiagram] = useState(false);
  // Pantalla completa del panel de detalle (expande el iframe a todo el viewport).
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = () => setIsFullscreen((v) => !v);
  // 🟩 P26.13 + P26.14 · Modales de diff y publicación.
  const [showDiff, setShowDiff] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  // 🟩 P26.30 · Versiones, vista móvil lado a lado y descarga ZIP.
  const [showVersions, setShowVersions] = useState(false);
  const [showSharePreview, setShowSharePreview] = useState(false);
  const [showMobileSplit, setShowMobileSplit] = useState(false);
  // 🟥 P26.40 · Deploy disparado DESDE la app generada (iframe → parent).
  useEffect(() => {
    const onMessage = (ev) => {
      if (ev?.data?.type === 'DAMYX_DEPLOY_REQUEST') setShowPublish(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const handleDownloadZip = async () => {
    if (!module || downloadingZip) return;
    setDownloadingZip(true);
    try {
      const res = await fetch(`${API_URL}/api/damyxia/modules/${module.id}/export-zip`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('fail');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(module.name || 'proyecto').replace(/[^a-zA-Z0-9._-]+/g, '-').toLowerCase()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(language === 'es' ? 'ZIP descargado' : 'ZIP downloaded');
    } catch {
      toast.error(language === 'es' ? 'No se pudo descargar el ZIP' : 'Could not download ZIP');
    } finally {
      setDownloadingZip(false);
    }
  };
  // 🟩 P26.24 · Editor inline para notas (edición directa sin abrir chat).
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // 🟩 P26.29 · Documentos: extraer el markdown del wrapper JSX para
  // mostrarlo formateado (secciones, listas) en el visor de documentos.
  const docMarkdown = React.useMemo(() => {
    if (!module || module.detected_type !== 'document') return '';
    const code = String(module.code || '');
    const m = code.match(/data-node-id="content">\{`([\s\S]*?)`\}<\/div>/);
    if (m && m[1].trim()) return m[1];
    // Documento guardado como markdown plano (sin wrapper JSX).
    if (!/^\s*import\s|function\s+\w+\s*\(/.test(code) && code.trim()) return code;
    return String(module.description || '');
  }, [module]);

  // 🟩 P26.26 · Editor inline habilitado para TODOS los módulos con code
  // (proyectos, notas, documents, tareas). Antes solo notes — ahora
  // cualquier trabajo del workspace puede editarse directamente sin
  // abrir el chat, incluyendo proyectos React.
  const isNoteLike = !!module && (
    typeof module.code === 'string' || typeof module.description === 'string'
  ) && !(Array.isArray(module.files) && module.files.length > 1);
  // ESC sale de fullscreen para no atrapar al usuario.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  // 🟥 CUADRO NEGRO 2026-07-06 — Vista por dispositivo desde el contexto
  // global WorkspaceContext. NO usa localStorage. NO usa eventos.
  const { deviceMode } = useWorkspace();

  // Detecta diagrama via metadata persistida o, como fallback para modulos
  // legacy creados antes de persistir `detected_type`, via firma del wrapper
  // React generado por /api/workspace/auto.
  const isDiagram =
    module?.detected_type === 'diagram' ||
    (typeof module?.code === 'string' &&
      module.code.includes('cdn.jsdelivr.net/npm/mermaid') &&
      /const\s+src\s*=\s*`/.test(module.code));

  const t = language === 'es' ? {
    editWithDamyxia: 'Editar con DamyxIA',
    viewCode: 'Ver Código / Exportar',
    editDiagram: 'Editar Diagrama',
    save: 'Guardar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    noModule: 'No hay trabajo activo. Selecciona uno en Mis Trabajos.',
    publish: 'Publicar proyecto',
    viewDiff: 'Ver cambios',
    editInline: '✏️ Editar aquí (sin chat)',
    saveNote: 'Guardar',
    cancelEdit: 'Cancelar',
    notePlaceholder: 'Edita el contenido del trabajo aquí…',
  } : {
    editWithDamyxia: 'Edit with DamyxIA',
    viewCode: 'View Code / Export',
    editDiagram: 'Edit Diagram',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    noModule: 'No active work. Pick one in My Works.',
    publish: 'Publish project',
    viewDiff: 'View changes',
    editInline: 'Edit here (no chat)',
    saveNote: 'Save',
    cancelEdit: 'Cancel',
    notePlaceholder: 'Write the note content…',
  };

  useEffect(() => {
    if (module) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEditName(module.name);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsEditingDiagram(false);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsEditingName(false);
      // 🟩 P26.24 · Reset del editor inline al cambiar de módulo.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsEditingNote(false);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNoteDraft(module.code || module.description || '');
    }
  }, [module]);

  const handleSaveNote = async () => {
    if (savingNote || !module) return;
    setSavingNote(true);
    try {
      await onUpdate(module.id, { code: noteDraft });
      setIsEditingNote(false);
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveName = async () => {
    if (!editName.trim()) return;
    setIsSaving(true);
    try {
      await onUpdate(module.id, { name: editName.trim() });
      setIsEditingName(false);
    } finally {
      setIsSaving(false);
    }
  };

  if (!module) {
    // 🟥 CUADRO NEGRO 2026-06 · WORKSPACE ÚNICO — Sin trabajo activo se muestra
    // UN mensaje claro con el acceso a la vista dedicada. Nunca una lista ni un
    // panel vacío.
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center gap-4 bg-slate-900/50 rounded-xl border border-slate-700 px-6 text-center"
        data-testid="workspace-no-active-work"
      >
        <p className="text-slate-400 text-sm">{t.noModule}</p>
        <button
          type="button"
          onClick={() => { window.location.href = '/mis-trabajos'; }}
          data-testid="workspace-go-my-works-btn"
          className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-xs font-semibold transition-colors"
        >
          {language === 'es' ? 'Ir a Mis Trabajos' : 'Go to My Works'}
        </button>
      </div>
    );
  }

  return (
    <div
      className={
        isFullscreen
          ? 'fixed inset-0 z-[100] flex flex-col bg-slate-900 overflow-hidden'
          : 'flex-1 flex flex-col bg-slate-900/50 overflow-hidden min-h-0'
      }
      data-testid="workspace-detail-panel"
    >
      {/* Header compacto — UNICO bloque visible arriba del iframe.
         Contiene el nombre del modulo (editable con doble clic) y los
         iconos de accion (Editar con DamyxIA, Ver Codigo, Editar Diagrama,
         Cerrar). Sin secciones "Nombre/Descripcion/Botones" debajo. */}
      <div className="relative z-50 flex items-center justify-between gap-2 px-3 py-1.5 border-b border-slate-700/70 bg-slate-800/95 backdrop-blur-sm shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Eye className="w-4 h-4 text-blue-400 shrink-0" />
          {isEditingName ? (
            <div className="flex items-center gap-1 flex-1">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') { setIsEditingName(false); setEditName(module.name); }
                }}
                data-testid="workspace-edit-name-input"
                className="flex-1 bg-slate-800 text-white border border-slate-600 rounded px-2 py-0.5 text-sm focus:border-blue-500 outline-none min-w-0"
                autoFocus
              />
              <button
                onClick={handleSaveName}
                disabled={isSaving}
                data-testid="workspace-save-name-btn"
                className="p-1 bg-green-500 rounded hover:bg-green-400 shrink-0"
                title={t.save}
              >
                <Check className="w-3.5 h-3.5 text-white" />
              </button>
              <button
                onClick={() => { setIsEditingName(false); setEditName(module.name); }}
                data-testid="workspace-cancel-name-btn"
                className="p-1 bg-slate-600 rounded hover:bg-slate-500 shrink-0"
                title={t.cancel}
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          ) : (
            <span
              className="text-sm font-medium text-white truncate cursor-pointer hover:text-blue-300"
              onDoubleClick={() => setIsEditingName(true)}
              data-testid="workspace-module-title"
              title={language === 'es' ? 'Doble clic para renombrar' : 'Double click to rename'}
            >
              {module.name}
            </span>
          )}
          {/* Boton expandir a pantalla completa — al lado del nombre. */}
          <button
            onClick={toggleFullscreen}
            data-testid="workspace-fullscreen-btn"
            title={
              isFullscreen
                ? (language === 'es' ? 'Salir de pantalla completa (Esc)' : 'Exit fullscreen (Esc)')
                : (language === 'es' ? 'Expandir a pantalla completa' : 'Expand to fullscreen')
            }
            aria-label={
              isFullscreen
                ? (language === 'es' ? 'Salir de pantalla completa' : 'Exit fullscreen')
                : (language === 'es' ? 'Expandir a pantalla completa' : 'Expand to fullscreen')
            }
            className="p-1 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-blue-300 transition-colors shrink-0"
          >
            {isFullscreen
              ? <Minimize2 className="w-4 h-4" strokeWidth={2.25} />
              : <Maximize2 className="w-4 h-4" strokeWidth={2.25} />}
          </button>
          {/* Ver codigo / exportar — al lado del boton de expandir,
             para que el usuario lo encuentre rapido junto al nombre. */}
          <button
            onClick={() => onViewCode(module)}
            data-testid="workspace-view-code-btn"
            title={t.viewCode}
            aria-label={t.viewCode}
            className="p-1 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-blue-300 transition-colors shrink-0"
          >
            <FileCode className="w-4 h-4" strokeWidth={2.25} />
          </button>
          {/* Cambiar tipo base — solo visible si el modulo tiene base_type
             (los grandfather no lo tienen aun). El boton DEBE ser explicito
             segun la regla 4 del Workspace. */}
          {module.base_type && (
            <button
              onClick={() => onChangeType && onChangeType(module)}
              data-testid="workspace-change-type-btn"
              title={
                module.type_locked
                  ? (language === 'es' ? 'Tipo fijado (Premium para cambiar)' : 'Type locked (Premium to change)')
                  : (language === 'es' ? `Cambiar tipo (actual: ${module.base_type})` : `Change type (current: ${module.base_type})`)
              }
              aria-label={language === 'es' ? 'Cambiar tipo' : 'Change type'}
              className={`hidden md:inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium border transition-colors shrink-0 ${
                module.type_locked
                  ? 'bg-slate-700 text-slate-400 border-slate-600 cursor-help'
                  : 'bg-amber-500/15 text-amber-300 border-amber-500/40 hover:bg-amber-500/25'
              }`}
            >
              {module.type_locked ? <Lock className="w-3 h-3" /> : <RefreshCw className="w-3 h-3" />}
              <span>{module.base_type}</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* 🟩 P26.30 · Historial de versiones (últimas 5). */}
          <button
            onClick={() => setShowVersions(true)}
            data-testid="workspace-versions-btn"
            title={language === 'es' ? 'Historial de versiones' : 'Version history'}
            aria-label={language === 'es' ? 'Historial de versiones' : 'Version history'}
            className="p-1.5 rounded-lg bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/40 transition-all"
          >
            <History className="w-4 h-4 text-indigo-300" />
          </button>
          {/* 🟩 P26.30 · Vista móvil junto a la de escritorio. */}
          <button
            onClick={() => setShowMobileSplit((v) => !v)}
            data-testid="workspace-mobile-view-btn"
            title={language === 'es' ? 'Ver también en móvil' : 'Show mobile view too'}
            aria-label={language === 'es' ? 'Ver también en móvil' : 'Show mobile view too'}
            className={`p-1.5 rounded-lg border transition-all ${
              showMobileSplit
                ? 'bg-cyan-400/25 border-cyan-300/70'
                : 'bg-cyan-500/15 hover:bg-cyan-500/25 border-cyan-500/40'
            }`}
          >
            <Smartphone className="w-4 h-4 text-cyan-300" />
          </button>
          {/* 🟩 P26.31 · Compartir vista previa con enlace temporal. */}
          <button
            onClick={() => setShowSharePreview(true)}
            data-testid="workspace-share-preview-btn"
            title={language === 'es' ? 'Compartir vista previa (enlace temporal)' : 'Share preview (temporary link)'}
            aria-label={language === 'es' ? 'Compartir vista previa (enlace temporal)' : 'Share preview (temporary link)'}
            className="p-1.5 rounded-lg bg-teal-500/15 hover:bg-teal-500/25 border border-teal-500/40 transition-all"
          >
            <Link2 className="w-4 h-4 text-teal-300" />
          </button>
          {/* 🟩 P26.30 · Descargar el proyecto como ZIP. */}
          <button
            onClick={handleDownloadZip}
            disabled={downloadingZip}
            data-testid="workspace-download-zip-btn"
            title={language === 'es' ? 'Descargar proyecto (ZIP)' : 'Download project (ZIP)'}
            aria-label={language === 'es' ? 'Descargar proyecto (ZIP)' : 'Download project (ZIP)'}
            className="p-1.5 rounded-lg bg-slate-700/60 hover:bg-slate-600 border border-slate-600 transition-all disabled:opacity-40"
          >
            {downloadingZip
              ? <RefreshCw className="w-4 h-4 text-slate-200 animate-spin" />
              : <Download className="w-4 h-4 text-slate-200" />}
          </button>
          {/* 🟩 P26.13 · Ver cambios (diff) — solo visible si hay snapshot previo. */}
          {module.diff_available && (module.previous_code || (Array.isArray(module.previous_files) && module.previous_files.length > 0)) && (
            <button
              onClick={() => setShowDiff(true)}
              data-testid="workspace-view-diff-btn"
              title={t.viewDiff}
              aria-label={t.viewDiff}
              className="p-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 transition-all"
            >
              <GitCompare className="w-4 h-4 text-amber-300" />
            </button>
          )}
          {/* 🟩 P26.24+P26.26 · Editor inline (lápiz amarillo) — visible en
              TODOS los módulos con code. Permite editar el proyecto/nota
              directamente sin abrir el chat con DamyxIA. */}
          {isNoteLike && !isEditingNote && (
            <button
              onClick={() => setIsEditingNote(true)}
              data-testid="workspace-edit-inline-btn"
              title={t.editInline}
              aria-label={t.editInline}
              className="p-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/35 border border-amber-400/60 transition-all shadow-sm shadow-amber-500/20"
            >
              <Edit3 className="w-4 h-4 text-amber-200" strokeWidth={2.5} />
            </button>
          )}
          {/* 🟩 P26.14 · Publicar proyecto — abre modal de publicación. */}
          <button
            onClick={() => setShowPublish(true)}
            data-testid="workspace-publish-btn"
            title={t.publish}
            aria-label={t.publish}
            className="p-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 transition-all"
          >
            <Rocket className="w-4 h-4 text-emerald-300" />
          </button>
          {/* Editar con DamyxIA — abre el chat con contexto del modulo. */}
          <button
            onClick={() => onRequestEdit(module)}
            data-testid="workspace-edit-with-ai-btn"
            title={t.editWithDamyxia}
            aria-label={t.editWithDamyxia}
            className="p-1.5 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-400 hover:to-blue-400 transition-all"
          >
            <Sparkles className="w-4 h-4 text-white" />
          </button>
          {/* Editar diagrama — solo si el modulo es detected_type=diagram. */}
          {isDiagram && !isEditingDiagram && (
            <button
              onClick={() => setIsEditingDiagram(true)}
              data-testid="edit-diagram-btn"
              title={t.editDiagram}
              aria-label={t.editDiagram}
              className="p-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 transition-all"
            >
              <Pencil className="w-4 h-4 text-emerald-300" />
            </button>
          )}
          {/* Cerrar el panel. */}
          <button
            onClick={onClose}
            data-testid="workspace-close-detail-btn"
            title={t.close}
            aria-label={t.close}
            className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors ml-1"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Contenido — iframe del preview.
          🟥 CUADRO NEGRO 2026-07-06 — Wrapper obligatorio `.device-preview {mode}`
          Ancho REAL (1280/768/375) via clases CSS externas. El iframe
          re-renderiza con `key={deviceMode}` para garantizar re-render en
          cada cambio. */}
      <div
        className="flex-1 flex flex-col overflow-auto min-h-0"
        data-testid="module-preview-sandbox"
        data-device-view={deviceMode}
        style={{
          background: deviceMode === 'desktop' ? undefined : '#020617',
          alignItems: deviceMode === 'desktop' ? 'stretch' : 'flex-start',
          padding: deviceMode === 'desktop' ? 0 : '12px',
        }}
      >
        <div
          className={`device-preview ${deviceMode} flex flex-col flex-1 min-h-0`}
          style={
            deviceMode === 'desktop'
              ? { width: '100%', maxWidth: '100%', border: 'none', background: 'transparent', margin: 0 }
              : { minHeight: '85%' }
          }
        >
        {isEditingNote && isNoteLike ? (
          // 🟩 P26.24 · Editor inline de notas — sin chat, edición directa.
          <div key={`note-edit-${module.id}`} className="flex-1 min-h-0 flex flex-col bg-slate-900 p-4 gap-3" data-testid="workspace-note-inline-editor">
            {/* P26.29 · Barra de acciones ARRIBA: antes quedaba al fondo del
                panel, fuera del área visible/clickable en pantallas cortas,
                y el guardado nunca se disparaba. */}
            <div className="flex items-center justify-between gap-2 shrink-0">
              <span className="text-[11px] uppercase tracking-widest text-amber-400">
                {t.editInline}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setIsEditingNote(false);
                    setNoteDraft(module.code || module.description || '');
                  }}
                  disabled={savingNote}
                  data-testid="workspace-note-cancel-btn"
                  className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium transition-colors disabled:opacity-40"
                >
                  {t.cancelEdit}
                </button>
                <button
                  onClick={handleSaveNote}
                  disabled={savingNote || !noteDraft.trim()}
                  data-testid="workspace-note-save-btn"
                  className="px-4 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {savingNote
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    : <Save className="w-3.5 h-3.5" />}
                  {t.saveNote}
                </button>
              </div>
            </div>
            <textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder={t.notePlaceholder}
              autoFocus
              spellCheck={false}
              className="flex-1 min-h-0 w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-[13px] font-mono text-slate-100 placeholder-slate-500 resize-none focus:outline-none focus:border-amber-500 leading-relaxed"
              data-testid="workspace-note-textarea"
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveNote();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setIsEditingNote(false);
                  setNoteDraft(module.code || module.description || '');
                }
              }}
            />
          </div>
        ) : Array.isArray(module?.project_files) && module.project_files.length > 0 ? (
          // 🟥 CUADRO NEGRO V2 — Proyectos multi-archivo: tree + editor inline + ZIP.
          // NO afecta a módulos legacy (cae al ramo siguiente si no hay project_files).
          <div key={`tree-${deviceMode}`} className="flex-1 min-h-0 overflow-hidden bg-slate-950">
            <ProjectTreeViewer
              projectFiles={module.project_files}
              projectName={module.name}
              moduleId={module.id}
              githubSource={module.github_source || null}
              autoSyncGithub={!!module.auto_sync_github}
              onFilesUpdate={(newFiles) => {
                // Notificar al padre para que actualice el módulo en memoria
                // SIN romper otros campos. Pasamos solo el delta.
                if (typeof onUpdate === 'function') {
                  onUpdate(module.id, { project_files: newFiles, __localOnly: true });
                }
              }}
            />
          </div>
        ) : isDiagram && isEditingDiagram ? (
          <Suspense fallback={null}>
            <DiagramEditor
              key={`diag-${deviceMode}`}
              module={module}
              onCancel={() => setIsEditingDiagram(false)}
              onSave={async (id, updates) => {
                await onUpdate(id, updates);
                setIsEditingDiagram(false);
              }}
            />
          </Suspense>
        ) : docMarkdown ? (
          // 🟩 P26.29 · Visor de DOCUMENTOS: markdown formateado nativo
          // (secciones, listas, contenido largo) en vez de texto plano
          // renderizado dentro del iframe.
          <div
            key={`doc-${deviceMode}`}
            className="flex-1 min-h-0 overflow-auto bg-slate-950 px-8 py-7"
            data-testid="workspace-document-viewer"
          >
            <div className="max-w-3xl">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-3">
                {language === 'es' ? 'Documento' : 'Document'}
              </div>
              <div
                className="damyx-doc text-[15px] leading-relaxed text-slate-200"
                data-testid="workspace-document-content"
              >
                <ReactMarkdown>{docMarkdown}</ReactMarkdown>
              </div>
            </div>
          </div>
        ) : (
          <div key={`preview-${module.id}-${deviceMode}`} className="flex-1 min-h-0 overflow-hidden relative bg-slate-900 flex">
            <div className="flex-1 min-w-0 relative">
              <UnifiedCodePreview
                code={buildRenderableCode(module)}
                title={module.name}
                height="100%"
                showWatermark={false}
                showControls={false}
                className="!rounded-none !border-0"
              />
            </div>
            {/* 🟩 P26.30 · Vista móvil junto a la de escritorio. */}
            {showMobileSplit && (
              <div
                className="shrink-0 w-[430px] border-l border-slate-700 bg-slate-950 flex flex-col items-center py-4 overflow-auto"
                data-testid="workspace-mobile-preview"
              >
                <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-widest text-cyan-300">
                  <Smartphone className="w-3.5 h-3.5" />
                  {language === 'es' ? 'Móvil · 390×780' : 'Mobile · 390×780'}
                </div>
                <div className="rounded-[2rem] border-4 border-slate-800 bg-black p-2 shadow-2xl">
                  <div className="w-[374px] h-[700px] overflow-hidden rounded-[1.5rem] bg-white">
                    <UnifiedCodePreview
                      code={buildRenderableCode(module)}
                      title={`${module.name} (mobile)`}
                      height="700px"
                      showWatermark={false}
                      showControls={false}
                      className="!rounded-none !border-0"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* 🟩 P26.13 + P26.14 · Modales lazy (solo se montan al abrirse) */}
      {showDiff && (
        <Suspense fallback={null}>
          <DiffModal
            module={module}
            language={language}
            onClose={() => setShowDiff(false)}
          />
        </Suspense>
      )}
      {showPublish && (
        <Suspense fallback={null}>
          <DeployModal
            module={module}
            renderableCode={buildRenderableCode(module)}
            language={language}
            onClose={() => setShowPublish(false)}
          />
        </Suspense>
      )}
      {showSharePreview && (
        <Suspense fallback={null}>
          <SharePreviewModal
            module={module}
            language={language}
            onClose={() => setShowSharePreview(false)}
          />
        </Suspense>
      )}
      {showVersions && (
        <Suspense fallback={null}>          <VersionsModal
            module={module}
            language={language}
            onClose={() => setShowVersions(false)}
            onRestored={async () => {
              // Refrescar el módulo en memoria tras el salto de versión.
              try {
                const res = await fetch(`${API_URL}/api/damyxia/modules/${module.id}`, {
                  credentials: 'include',
                });
                const fresh = await res.json();
                if (fresh && fresh.id && typeof onUpdate === 'function') {
                  onUpdate(module.id, { ...fresh, __localOnly: true });
                }
              } catch { /* el listado se refresca solo al reabrir */ }
            }}
          />
        </Suspense>
      )}
    </div>
  );
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================


export default ModuleDetailPanel;
